import React, {FC} from "react";
import {useAccountStore} from "../account-store";
import {AccountPageWrapper} from "../components/common";
import {useIntl} from 'react-intl';
import {useAuthStore} from "../../authentification/auth-store";
import {useBookingStore} from "../../../booking/booking-store";
import {useConfirm} from "../../../common/providers/confirm-provider";
import {generatePath} from "react-router";
import {routeUserAccount} from "../../../routes";
import {useCodeBouton} from "../../../common/providers/code-bouton-provider";
import {useTimezone} from "../../../common/providers/timezone-provider";
import {useConfigurationStore} from "../../../configuration/configuration-store";
import {AppointmentStatusList} from "../components/appointment-status-list";
import {ASC, DESC, statusToValid, statusValid, statusWaiting} from "../account.types";

export const AppointmentsListingPage: FC = () => {
    const {fetchAppointmentsList, resetAppointments} = useAccountStore()
    const config = useConfigurationStore();
    const {timezone} = useTimezone();
    const {logOut} = useAuthStore();
    const {cancelAppointment} = useBookingStore();
    const intl = useIntl();
    const {openConfirm} = useConfirm();
    const codeBouton = useCodeBouton();
    const today = new Date();

    const onLogoutClick = async () => {
        await openConfirm({
            content: intl.formatMessage({
                id: 'accountPageLogoutConfirmation',
                defaultMessage: "Êtes-vous sûr(e) ?"
            }), title: intl.formatMessage({id: 'accountPageLogoutText', defaultMessage: "Déconnexion"})
        });
        logOut();
    }

    const isCancelable = (rdvDate: string) => {
        const cancelRdvDelay = config.appointment?.cancelRdvDelay;
        if (cancelRdvDelay) {
            const now = new Date();
            const appointmentDate = new Date(rdvDate);

            // Parse the delay value and unit
            const match = cancelRdvDelay.match(/(\d+)\s*(days|months|hours|h|d|m)/i);
            if (match) {
                const value = parseInt(match[1]);
                const unit = match[2].toLowerCase();

                let cancelDeadline = new Date(now);
                switch (unit) {
                    case 'days':
                    case 'd':
                        cancelDeadline.setDate(now.getDate() + value);
                        break;
                    case 'months':
                    case 'm':
                        cancelDeadline.setMonth(now.getMonth() + value);
                        break;
                    case 'hours':
                    case 'h':
                        cancelDeadline.setHours(now.getHours() + value);
                        break;
                }
                return appointmentDate > cancelDeadline;
            }
        }
        return true;
    }

    return <AccountPageWrapper
        backLinkUrl={generatePath(routeUserAccount, {codeBouton, locale: intl.locale})}
        onLogoutClick={onLogoutClick}
        pageTitle={intl.formatMessage({
            id: 'AccountPageAppointmentsTitle',
            defaultMessage: 'Vos rendez-vous'
        })}>

        <AppointmentStatusList
            fetchAppointmentsList={(offset) => fetchAppointmentsList(offset, statusToValid, ASC)}
            resetAppointments={resetAppointments}
            icon={"question-mark-outline"}
            timezone={timezone}
            showTotal={true}
            isCancelable={isCancelable}
            cancelAppointment={cancelAppointment}
            title={intl.formatMessage({
                id: 'AccountPageAppointmentsAwaitingApprovalTitle',
                defaultMessage: 'rendez-vous en attente de validation'
            })}
        />

        <AppointmentStatusList
            fetchAppointmentsList={(offset) => fetchAppointmentsList(offset, statusValid, ASC, today)}
            resetAppointments={resetAppointments}
            icon={"bookmark-outline"}
            timezone={timezone}
            showTotal={true}
            isCancelable={isCancelable}
            cancelAppointment={cancelAppointment}
            title={intl.formatMessage({
                id: 'AccountPageUpcomingAppointmentsTitle',
                defaultMessage: 'rendez-vous à venir'
            })}
        />

        <AppointmentStatusList
            fetchAppointmentsList={(offset) => fetchAppointmentsList(offset, statusWaiting, ASC)}
            resetAppointments={resetAppointments}
            icon={"wait-outline"}
            timezone={timezone}
            showTotal={true}
            isCancelable={isCancelable}
            cancelAppointment={cancelAppointment}
            title={intl.formatMessage({
                id: 'AccountPageAppointmentsWaitingListTitle',
                defaultMessage: 'rendez-vous sur liste d’attente'
            })}
        />

        <AppointmentStatusList
            fetchAppointmentsList={(offset) => fetchAppointmentsList(offset, statusValid, DESC, undefined, today)}
            resetAppointments={resetAppointments}
            icon={"menu-2-fill"}
            timezone={timezone}
            showTotal={false}
            cancelAppointment={cancelAppointment}
            title={intl.formatMessage({
                id: 'AccountPageAppointmentsHistoryTitle',
                defaultMessage: 'Historique des rendez-vous '
            })}
        />

    </AccountPageWrapper>
}
